import React from "react";
import PropTypes from "prop-types";
import Book from "./index";
import { connect } from "react-redux";
import { selectors as routerSelectors } from "../../../../redux/router";
import { actions as favoritesActions } from "../../../../redux/favorites";
import { selectors as favoritesSelectors } from "../../../../redux/favorites";
import { selectors as bridgeSelectors } from "../../../../redux/bridge";
import { actions as bookActions } from "../../../../redux/book";
import { actions as bridgeActions } from "../../../../redux/bridge";
import { t } from "../../../core/translate";
import {
  Attachment,
  Search,
  ICircle,
  Heart,
  HeartFill
} from "@vitalsource/vstui-icons";
import { selectors as brandSelectors } from "../../../../redux/brand";
import { actions as uiLibraryActions } from "../../../../redux/ui/library";
import {
  getDurationMessage,
  getAvailabilityMessage
} from "./bridge-book-labels";
import LibraryHelpers from "../../../../stores/library-helpers";
import { showTranslatedEdition } from "../../../../redux/bridge/utils";

const BridgeBook = ({
  title,
  coverImageUrl,
  isbn,
  width,
  height,
  active,
  returnable,
  launchBook,
  returnBook,
  showFavorites,
  addToFavorite,
  deleteFromFavorite,
  brand,
  openSearchPanel,
  openBookPanel,
  isCreditAsset,
  isConcurrencyAsset,
  isReturnable,
  isAvailableToBorrow,
  hasAvailableCopies,
  hasAvailableUserCredits,
  hasAvailableUserCreditsForAsset,
  termsDuration,
  termsAbsDate,
  expirationTimestamp,
  adoptions,
  hasAncillaries,
  openAncillaryPanel,
  native,
  nativePlatform,
  distributable,
  ...props
}) => {
  const primaryButtons = [];
  const secondaryButtons = [];
  const showTranslated = showTranslatedEdition(props.edition);
  const bookTitle = showTranslated
    ? `${title}, ${t("library.book.edition-number", "Edition", {
        edition: props.edition
      })}`
    : title;

  const showFavoriteBookToast = () => {
    LibraryHelpers.favoriteBookToast(title, isbn);
  };

  const showUnFavoriteBookToast = () => {
    LibraryHelpers.unFavoriteBookToast(title, isbn);
  };

  const isFavorite = props.favorite ? props.favorite : false;

  if (active || (distributable && !isCreditAsset && !isConcurrencyAsset)) {
    primaryButtons.push({
      type: "transparent",
      label: t("library.open-book"),
      onClick: launchBook,
      href: `#/books/${isbn}/`
    });
    if (isReturnable && active) {
      primaryButtons.push({
        type: "transparent",
        label: t("library.return-book"),
        onClick: () => {
          returnBook();
          LibraryHelpers.returnBookToast(title, isbn);
        }
      });
      secondaryButtons.push({
        onClick: () => openBookPanel(title),
        label: t("library.book-details"),
        icon: ICircle
      });
    } else {
      primaryButtons.push({
        type: "transparent",
        label: t("library.book-details"),
        onClick: () => openBookPanel(title)
      });
    }
  }
  if (!active && !distributable) {
    primaryButtons.push({
      type: "transparent",
      label: t("library.view-details"),
      onClick: () => openBookPanel(title)
    });
  } else if ((isCreditAsset || isConcurrencyAsset) && !active) {
    if (isAvailableToBorrow) {
      primaryButtons.push({
        type: "white",
        label: t("library.borrow"),
        onClick: () => openBookPanel(title)
      });
      secondaryButtons.push({
        onClick: () => openBookPanel(title),
        label: t("library.book-details"),
        icon: ICircle
      });
    } else {
      primaryButtons.push({
        type: "transparent",
        label: t("library.view-details"),
        onClick: () => openBookPanel(title)
      });
    }
  }

  if (
    hasAncillaries &&
    (!native || nativePlatform === "web") &&
    (distributable || active)
  ) {
    secondaryButtons.push({
      onClick: () => openAncillaryPanel(title),
      label: t("filter.resources", "Resources"),
      icon: Attachment
    });
  }

  if (brand.Search.enabled && (distributable || active)) {
    secondaryButtons.push({
      onClick: () => openSearchPanel(title),
      label: t("base.search", "Search"),
      icon: Search
    });
  }

  if (showFavorites && isFavorite) {
    secondaryButtons.push({
      favorite: true,
      onClick: () => {
        deleteFromFavorite(isbn);
        showUnFavoriteBookToast();
      },
      label: t("explore.remove-from-favorites", "Remove from Favorites"),
      icon: HeartFill
    });
  } else if (showFavorites && !isFavorite) {
    secondaryButtons.push({
      favorite: false,
      onClick: () => {
        addToFavorite(isbn);
        showFavoriteBookToast();
      },
      label: t("explore.add-to-favorites", "Add to Favorites"),
      icon: Heart
    });
  }

  const durationMessage = getDurationMessage(
    isCreditAsset,
    termsDuration,
    termsAbsDate,
    expirationTimestamp,
    active,
    distributable
  );
  const availabilityMessage = getAvailabilityMessage(
    hasAvailableCopies,
    hasAvailableUserCreditsForAsset,
    active,
    distributable
  );
  return (
    <Book
      {...props}
      title={bookTitle}
      isbn={isbn}
      height={height ? parseInt(height) : undefined}
      width={width ? parseInt(width) : undefined}
      primary={primaryButtons}
      onClick={primaryButtons.length ? primaryButtons[0].onClick : launchBook}
      secondary={secondaryButtons}
      durationMessage={durationMessage}
      availabilityMessage={availabilityMessage}
    />
  );
};

const mapLicenseStateToProps = (state, props) => {
  return {
    ...bridgeSelectors.getAsset(state, props.isbn),
    native: routerSelectors.isNative(state),
    nativePlatform: routerSelectors.getQueryParam(state, "platform"),
    brand: brandSelectors.getBrand(state),
    isReturnable: bridgeSelectors.isReturnable(state, props.isbn),
    isCreditAsset: bridgeSelectors.isCreditAsset(state, props.isbn),
    isAvailableToBorrow: bridgeSelectors.isAvailableToBorrow(state, props.isbn),
    isConcurrencyAsset: bridgeSelectors.isConcurrencyAsset(state, props.isbn),
    hasAvailableCopies: bridgeSelectors.hasAvailableCopies(state, props.isbn),
    hasAvailableUserCredits: bridgeSelectors.hasAvailableUserCredits(state),
    hasAvailableUserCreditsForAsset: bridgeSelectors.hasAvailableUserCreditsForAsset(
      state,
      props.isbn
    ),
    termsDuration: bridgeSelectors.getTermsDuration(state, props.isbn),
    termsAbsDate: bridgeSelectors.getTermsAbsDate(state, props.isbn),
    expirationTimestamp: bridgeSelectors.getExpirationTimestamp(
      state,
      props.isbn
    ),
    distributable: bridgeSelectors.isDistributable(state, props.isbn),
    showFavorites: favoritesSelectors.favoritesEnabled(state)
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  launchBook: () =>
    dispatch(
      bookActions.launchBridgeBook(ownProps.isbn, {
        context: ownProps.context,
        bookIndex: ownProps.index
      })
    ),
  openSearchPanel: title =>
    dispatch(uiLibraryActions.openSearchPanel(ownProps.isbn, title)),
  openAncillaryPanel: title =>
    dispatch(uiLibraryActions.openSimpleResourcesPanel(ownProps.isbn, title)),
  openBookPanel: title =>
    dispatch(uiLibraryActions.openSimpleBookPanel(ownProps.isbn, title)),
  returnBook: () => dispatch(bridgeActions.returnAsset(ownProps.isbn)),
  addToFavorite: () => dispatch(favoritesActions.addFavorite(ownProps.isbn)),
  deleteFromFavorite: () =>
    dispatch(favoritesActions.deleteFavorite(ownProps.isbn))
});

BridgeBook.propTypes = {
  isbn: PropTypes.string.isRequired,
  context: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired
};

export default connect(mapLicenseStateToProps, mapDispatchToProps)(BridgeBook);
